import {
  Alert,
  Box,
  Button,
  Card,
  Divider,
  Group,
  LoadingOverlay,
  Modal,
  Stack,
  Text,
} from '@mantine/core';
import { ReactNode, useState, useEffect } from 'react';
import { AlertCircle, Save, Sparkles, Trash } from 'lucide-react';
import { useDisclosure } from '@mantine/hooks';
import { useForm, FormProvider } from 'react-hook-form';
import { PromptEditor } from './PromptEditor';
import { htmlToTextFormat } from './PromptEditor/utils';
import { notifications } from '@mantine/notifications';
import { generateConfigDescription } from '../../api/configurations/configDescriptionGenerator';
import { callOpenAI } from '../../api/configurations/client';
import {
  generateConfigPrompt,
  SYSTEM_PROMPTS,
} from '../../api/configurations/prompts';
import { extractJSON } from '../../api/translations/utils';

type SessionType = 'scenario' | 'esperienza';

export type PromptOptionGroup = {
  group: string;
  items: Array<{
    value: string;
    label: string;
    subtitle?: string;
    icon?: string;
    description?: string;
    videoId?: string;
  }>;
};

interface SessionConfigurationProps {
  sessionType: SessionType;
  activeStep: number;
  onReset: () => void;
  importTemplateTrigger?: ReactNode;
  onSubmitForm?: () => void;
  saveButtonLoading?: boolean;
  saveButtonDisabled?: boolean;
  saveButtonHint?: string;
  configForm: ReactNode;
  onConfigGenerated?: (
    config: any,
    descriptions: {
      title: string;
      shortDescription: string;
      description: string;
    }
  ) => void;
  isConfigEnabled?: boolean;
  configDisabledContent?: ReactNode;
  promptVideoOptions?: PromptOptionGroup[];
  promptAudioOptions?: PromptOptionGroup[];
  isFormDisabled?: boolean;
}

export function SessionConfiguration({
  sessionType,
  activeStep,
  onReset,
  importTemplateTrigger,
  onSubmitForm,
  saveButtonLoading,
  saveButtonDisabled,
  saveButtonHint,
  configForm,
  onConfigGenerated,
  isConfigEnabled = true,
  configDisabledContent,
  promptVideoOptions,
  promptAudioOptions,
  isFormDisabled = false,
}: SessionConfigurationProps) {
  const isActive = activeStep === 3;
  const [opened, { open, close }] = useDisclosure(false);
  const [isGenerating, setIsGenerating] = useState(false);

  const aiForm = useForm({
    defaultValues: {
      prompt: '',
    },
  });

  // Reset del form quando la modale viene chiusa
  useEffect(() => {
    if (!opened) {
      aiForm.reset();
    }
  }, [opened, aiForm]);

  const handleGenerateAI = async () => {
    const prompt = aiForm.getValues('prompt');
    const promptText = htmlToTextFormat(prompt);

    if (!promptText.trim()) {
      notifications.show({
        color: 'yellow',
        title: 'Descrizione richiesta',
        message:
          'Inserisci una descrizione della sessione per generare la configurazione',
      });
      return;
    }

    setIsGenerating(true);
    try {
      // Generate configuration using OpenAI directly
      notifications.show({
        id: 'generating-config',
        loading: true,
        title: 'Generazione configurazione...',
        message: 'Sto generando la configurazione con AI...',
        autoClose: false,
        withCloseButton: false,
      });

      const prompt = generateConfigPrompt(promptText, sessionType);

      const { content } = await callOpenAI(
        [
          {
            role: 'system',
            content: SYSTEM_PROMPTS.CONFIG_GENERATOR,
          },
          {
            role: 'user',
            content: prompt,
          },
        ],
        {
          model: 'gpt-4o-mini', // Most economical model
          temperature: 0.7,
          maxTokens: 2000,
        }
      );

      if (!content) {
        throw new Error('No response from OpenAI');
      }

      // Extract and parse JSON from response
      const result = extractJSON(content);

      // Validate basic structure
      if (
        !result.steps ||
        !Array.isArray(result.steps) ||
        result.steps.length === 0
      ) {
        throw new Error('Invalid configuration: missing or empty steps');
      }

      notifications.update({
        id: 'generating-config',
        loading: false,
        title: 'Configurazione generata',
        message: 'Generazione descrizioni in corso...',
        autoClose: 3000,
      });

      // Generate title, shortDescription, and description using OpenAI
      notifications.show({
        id: 'generating-descriptions',
        loading: true,
        title: 'Generazione descrizioni...',
        message: 'Sto generando titolo e descrizioni per la configurazione...',
        autoClose: false,
        withCloseButton: false,
      });

      try {
        // Pass the entire result (which is the config) to generate descriptions
        const descriptions = await generateConfigDescription(result);

        notifications.update({
          id: 'generating-descriptions',
          loading: false,
          color: 'green',
          title: 'Configurazione generata',
          message:
            'La configurazione è stata generata con successo. Verifica i dettagli e salva quando sei soddisfatto.',
          autoClose: 5000,
        });

        // Call callback with generated config and descriptions
        if (onConfigGenerated) {
          onConfigGenerated(result, descriptions);
        }

        // Reset del form prima di chiudere la modale
        aiForm.reset();
        close();
      } catch (descError) {
        console.error('Error generating descriptions:', descError);
        notifications.update({
          id: 'generating-descriptions',
          loading: false,
          color: 'yellow',
          title: 'Configurazione generata',
          message:
            'La configurazione è stata generata, ma non è stato possibile generare le descrizioni automaticamente.',
          autoClose: 5000,
        });

        // Call callback anyway with empty descriptions
        if (onConfigGenerated) {
          onConfigGenerated(result, {
            title: '',
            shortDescription: '',
            description: '',
          });
        }

        aiForm.reset();
        close();
      }
    } catch (error) {
      console.error('Error generating configuration:', error);

      // Hide any loading notifications
      notifications.hide('generating-config');
      notifications.hide('generating-descriptions');

      let errorMessage = 'Errore durante la generazione della configurazione';

      if (error instanceof Error) {
        // Log completo dell'errore per debug
        console.error('Full error details:', {
          message: error.message,
          stack: error.stack,
        });

        // Controlla se è un errore di API key
        if (
          error.message.includes('invalid_api_key') ||
          error.message.includes('Incorrect API key') ||
          error.message.includes('401') ||
          (error.message.includes('API') && error.message.includes('key'))
        ) {
          errorMessage =
            'Chiave API OpenAI non valida o non configurata. Verifica che VITE_OPENAI_API_KEY sia impostata correttamente nel file .env e riavvia il server di sviluppo.';
        } else {
          // Mostra il messaggio originale dell'errore
          errorMessage = error.message;
        }
      }

      notifications.show({
        color: 'red',
        title: 'Errore',
        message: errorMessage,
        autoClose: 10000,
      });
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Card
      withBorder
      p='md'
      style={{
        opacity: isActive ? 1 : 0.8,
        position: 'relative',
      }}
    >
      <LoadingOverlay visible={isFormDisabled} />
      <Stack gap='md'>
        <Group justify='space-between' align='center'>
          <Text fw={500} size='lg'>
            4. Configura{' '}
            {sessionType === 'scenario' ? 'Scenario' : 'Esperienza'}
          </Text>
          {sessionType === 'esperienza' && (
            <Group gap='md' align='center'>
              {/* Sezione AI */}
              <Group gap='xs'>
                <Button
                  size='xs'
                  variant='light'
                  gradient={{ from: 'violet', to: 'cyan', deg: 90 }}
                  onClick={open}
                  leftSection={<Sparkles size={14} />}
                  style={{
                    background:
                      'linear-gradient(90deg, var(--mantine-color-violet-0), var(--mantine-color-cyan-0))',
                    color: 'var(--mantine-color-violet-7)',
                  }}
                >
                  Genera con AI
                </Button>
              </Group>

              <Divider orientation='vertical' />

              {/* Sezione Template */}
              <Group gap='xs'>
                {importTemplateTrigger}
                {onSubmitForm && (
                  <Button
                    size='xs'
                    variant='light'
                    color='blue'
                    onClick={onSubmitForm}
                    leftSection={<Save size={14} />}
                    loading={saveButtonLoading}
                    disabled={saveButtonDisabled}
                    title='Salva questa configurazione come template per riutilizzarla in futuro'
                  >
                    {saveButtonHint ?? 'Salva come Template'}
                  </Button>
                )}
              </Group>

              {/* Sezione Azioni */}
              <Group gap='xs'>
                <Button
                  size='xs'
                  variant='subtle'
                  color='red'
                  onClick={onReset}
                  leftSection={<Trash size={14} />}
                >
                  Reset
                </Button>
              </Group>
            </Group>
          )}
        </Group>
        {sessionType === 'scenario' ? (
          <>
            <Text size='sm' c='dimmed'>
              Personalizza lo scenario selezionando la modalità desiderata e
              configurando i relativi parametri.
            </Text>
            {isConfigEnabled ? (
              <Box
                style={{
                  opacity: isFormDisabled ? 0.6 : 1,
                  pointerEvents: isFormDisabled ? 'none' : 'auto',
                }}
              >
                {configForm}
              </Box>
            ) : (
              (configDisabledContent ?? (
                <Alert
                  variant='light'
                  color='yellow'
                  icon={<AlertCircle size={18} />}
                >
                  Non hai prodotti attivi per questo scenario. Attiva almeno un
                  prodotto compatibile per procedere con la configurazione.
                </Alert>
              ))
            )}
          </>
        ) : (
          <>
            <Text size='sm' c='dimmed'>
              Crea step condizionali per configurazioni complesse. Seleziona
              scenari, esperienze, audio e domande con logica condizionale.
            </Text>
            {isConfigEnabled ? (
              <Box
                style={{
                  opacity: isFormDisabled ? 0.6 : 1,
                  pointerEvents: isFormDisabled ? 'none' : 'auto',
                }}
              >
                {configForm}
              </Box>
            ) : (
              (configDisabledContent ?? (
                <Alert
                  variant='light'
                  color='yellow'
                  icon={<AlertCircle size={18} />}
                >
                  Non hai prodotti attivi per questa esperienza. Attiva almeno
                  un prodotto compatibile per procedere con la configurazione.
                </Alert>
              ))
            )}
          </>
        )}
      </Stack>

      <Modal
        opened={opened}
        centered
        onClose={close}
        title={
          <Group gap='xs' align='center'>
            <Text size='lg'>
              Genera una Esperienza con l'assistenza dell'AI
            </Text>
            <Text fw={500} style={{ textTransform: 'uppercase' }}></Text>
          </Group>
        }
        size='xl'
        closeOnClickOutside={!isGenerating}
        closeOnEscape={!isGenerating}
      >
        <Box pos='relative'>
          <LoadingOverlay
            visible={isGenerating}
            zIndex={1000}
            overlayProps={{ radius: 'sm', blur: 2 }}
          />
          <FormProvider {...aiForm}>
            <form
              onSubmit={aiForm.handleSubmit(handleGenerateAI)}
              style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
            >
              <Stack gap='md'>
                <Text size='sm' c='dimmed'>
                  Descrivi testualmente l'esperienza che vorresti creare.
                  Seleziona i video e gli audio dalla lista usando i campi di
                  selezione (i video disponibili sono quelli presenti nella tua
                  subscription). L'AI creerà automaticamente gli step
                  correttamente interpretando il tuo testo.
                </Text>
                <PromptEditor
                  value={aiForm.watch('prompt')}
                  onChange={html =>
                    aiForm.setValue('prompt', html, { shouldValidate: true })
                  }
                  placeholder='Esempio: Crea una sessione per gestire l&#39;ansia da colloquio di lavoro. Inizia con un video introduttivo, poi inserisci una domanda per capire come ti senti, e continua con contenuti personalizzati basati sulla risposta...'
                  videoOptions={promptVideoOptions}
                  audioOptions={promptAudioOptions}
                />
                <Text size='xs' c='dimmed' style={{ lineHeight: 1.6 }}>
                  <Alert
                    color='gray.5'
                    icon={<AlertCircle size={16} />}
                    variant='light'
                    radius='sm'
                    style={{ alignItems: 'flex-start' }}
                  >
                    <Text size='xs'>
                      La configurazione generata dall&apos;AI è interpretativa:
                      rivedi e personalizza i dettagli prima di salvare per
                      assicurarti che rispecchi le tue esigenze.
                    </Text>
                  </Alert>
                </Text>
                <Group justify='flex-end' gap='xs'>
                  <Button
                    variant='subtle'
                    size='md'
                    onClick={close}
                    disabled={isGenerating}
                  >
                    Annulla
                  </Button>
                  <Button
                    type='submit'
                    variant='light'
                    gradient={{ from: 'violet', to: 'cyan', deg: 90 }}
                    loading={isGenerating}
                    leftSection={<Sparkles size={16} />}
                    size='md'
                    style={{
                      background:
                        'linear-gradient(90deg, var(--mantine-color-violet-0), var(--mantine-color-cyan-0))',
                      color: 'var(--mantine-color-violet-7)',
                    }}
                  >
                    Genera con AI
                  </Button>
                </Group>
              </Stack>
            </form>
          </FormProvider>
        </Box>
      </Modal>
    </Card>
  );
}
